import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { walletAPI, gstRuleAPI, vendorProfileAPI, razorpayAPI } from '../../services/api';
import { DashboardSkeleton } from '../../components/skeletons';
import { useAuth } from '../../context/AuthContext';

const PRESETS = [500, 1000, 2000, 5000, 10000];

const S = {
  page: { padding: '24px 20px', background: 'var(--cream)', minHeight: '100vh' },
  title: { fontFamily: 'var(--font-serif)', fontSize: 32, fontWeight: 700, color: 'var(--char)', marginBottom: 4 },
  subtitle: { fontSize: 13, color: 'var(--muted)', marginBottom: 24 },
  balanceCard: {
    padding: '32px 36px',
    borderRadius: 16,
    background: 'linear-gradient(135deg, #1B5C3A 0%, #0F3D26 100%)',
    color: '#fff',
    marginBottom: 24,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 16,
    boxShadow: '0 8px 24px rgba(27,92,58,0.2)'
  },
  balanceLabel: { fontSize: 12, opacity: 0.8, marginBottom: 6, textTransform: 'uppercase', letterSpacing: 1, fontWeight: 600 },
  balanceVal: { fontFamily: 'var(--font-serif)', fontSize: 40, fontWeight: 700, letterSpacing: 0.5 },
  rechargeCard: { padding: '24px', borderRadius: 14, background: '#fff', border: '1.5px solid var(--border)', marginBottom: 24, boxShadow: 'var(--shadow-sm)' },
  sectionTitle: { fontSize: 18, fontWeight: 700, color: 'var(--char)', marginBottom: 16, fontFamily: 'var(--font-serif)' },
  presetRow: { display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 20 },
  presetBtn: (active) => ({
    padding: '10px 20px', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer',
    border: active ? '2.5px solid #1B5C3A' : '1.5px solid var(--border)',
    background: active ? 'var(--green-lt)' : '#fff', color: active ? '#1B5C3A' : 'var(--char)',
    transition: 'all 0.15s ease'
  }),
  customRow: { display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 20 },
  input: { padding: '10px 14px', borderRadius: 8, border: '1.5px solid var(--border)', fontSize: 14, width: 220, boxSizing: 'border-box' },
  table: { width: '100%', borderCollapse: 'collapse', minWidth: 500 },
  th: { textAlign: 'left', padding: '12px 16px', fontSize: 11, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: 0.6, borderBottom: '2.5px solid var(--border)', backgroundColor: '#FAF8F3' },
  td: { padding: '12px 16px', fontSize: 13, color: 'var(--char)', borderBottom: '1px solid var(--border)', transition: 'background-color 0.15s ease' },
  emptyBox: { textAlign: 'center', padding: '48px 20px', background: '#fff', borderRadius: 14, border: '1.5px solid var(--border)' },
};

// ── Load Razorpay Checkout JS script dynamically ─────────────────────────────
const loadRazorpayScript = () =>
  new Promise((resolve) => {
    if (document.getElementById('razorpay-checkout-js')) { resolve(true); return; }
    const script = document.createElement('script');
    script.id = 'razorpay-checkout-js';
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });

export default function Wallet() {
  const { user } = useAuth();
  const [wallet, setWallet] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAmount, setSelectedAmount] = useState(null);
  const [customAmount, setCustomAmount] = useState('');
  const [recharging, setRecharging] = useState(false);
  const [rechargeStatus, setRechargeStatus] = useState(''); // status message shown under button
  const [gstRule, setGstRule] = useState(null);
  const [lastInvoice, setLastInvoice] = useState(null);
  const [vendorProfile, setVendorProfile] = useState(null);

  const fetchData = async () => {
    try {
      const [wRes, tRes, gRes, pRes] = await Promise.all([
        walletAPI.me(),
        walletAPI.transactions(),
        gstRuleAPI.active(),
        vendorProfileAPI.me()
      ]);

      const wData = wRes.data.data;
      const tData = tRes.data.data || [];
      setWallet(wData);
      setGstRule(gRes.data.data);
      setVendorProfile(pRes.data.data);

      // Process transactions to include running balance
      let current = wData.balance;
      const processed = tData.map(t => {
        const tWithBal = { ...t, runningBalance: current };
        if (t.type === 'credit' || t.type === 'refund') {
          current -= t.amount;
        } else {
          current += t.amount;
        }
        return tWithBal;
      });
      setTransactions(processed);
    } catch (err) {
      console.error('Failed to load wallet data:', err);
    } finally {
      setLoading(false);
    }
  };

  const [requesting, setRequesting] = useState(false);

  const handleRequestCredit = async () => {
    setRequesting(true);
    try {
      const res = await walletAPI.requestCredit();
      await fetchData();
      alert(res.data?.message || '✅ Credit request submitted successfully.');
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to submit credit request.');
    } finally {
      setRequesting(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const kycStatus = vendorProfile?.kyc?.status || 'draft';
  const kycVerified = kycStatus === 'verified';

  const isLocalMode = wallet?.systemMode === 'local_production';

  const vendorState = (user?.state || '').toLowerCase();
  const isIntraState = gstRule && vendorState === (gstRule.platformState || 'gujarat').toLowerCase();
  const gstPreview = (gross) => {
    if (!gstRule || !gross) return null;
    const rate = gstRule.totalRate / 100;
    const taxable = Math.round((gross / (1 + rate)) * 100) / 100;
    const gst = Math.round((gross - taxable) * 100) / 100;
    return { taxable, gst, isIntraState };
  };

  const rechargeAmount = selectedAmount || (customAmount ? Number(customAmount) : 0);

  // ── Step 1: Create Razorpay order → open checkout ────────────────────────
  const handlePayNow = async () => {
    if (!rechargeAmount || rechargeAmount <= 0) return;
    const minLimit = wallet?.minRecharge || 500;
    if (rechargeAmount < minLimit) { alert(`Minimum recharge amount is ₹${minLimit}.`); return; }

    setRecharging(true);
    setRechargeStatus('Creating payment order...');

    try {
      // Load Razorpay script
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        alert('Failed to load Razorpay SDK. Check your internet connection.');
        setRecharging(false);
        setRechargeStatus('');
        return;
      }

      // Create order on backend
      const orderRes = await razorpayAPI.createOrder({ amount: rechargeAmount });
      if (!orderRes.data?.success) {
        alert(orderRes.data?.message || 'Failed to create payment order.');
        setRecharging(false);
        setRechargeStatus('');
        return;
      }

      const { orderId, keyId, amount, vendorName, vendorEmail } = orderRes.data.data;
      setRechargeStatus('Opening payment...');

      // ── Step 2: Open Razorpay Checkout ──────────────────────────────────
      const options = {
        key: keyId,
        amount: Math.round(amount * 100), // paise
        currency: 'INR',
        name: 'Kanjusee',
        description: 'Wallet Recharge',
        order_id: orderId,
        prefill: {
          name: vendorName || user?.fullName || user?.name || '',
          email: vendorEmail || user?.email || '',
        },
        theme: { color: '#1B5C3A' },
        modal: {
          ondismiss: () => {
            setRecharging(false);
            setRechargeStatus('');
          },
        },
        handler: async (response) => {
          // ── Step 3: Verify payment on backend ────────────────────────────
          setRechargeStatus('Verifying payment...');
          try {
            const verifyRes = await razorpayAPI.verifyPayment({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              amount: amount,
            });

            if (!verifyRes.data?.success) {
              alert(verifyRes.data?.message || 'Payment verification failed.');
              setRecharging(false);
              setRechargeStatus('');
              return;
            }

            const d = verifyRes.data.data || {};
            setLastInvoice(d.invoice || null);
            setSelectedAmount(null);
            setCustomAmount('');
            setRechargeStatus('');
            // Refresh wallet data
            await fetchData();
            alert(`✅ ₹${amount.toLocaleString('en-IN')} added to your wallet successfully!`);
          } catch (err) {
            alert(err.response?.data?.message || 'Payment verification failed. Contact support.');
          } finally {
            setRecharging(false);
            setRechargeStatus('');
          }
        },
      };

      // eslint-disable-next-line no-undef
      const rzp = new Razorpay(options);
      rzp.on('payment.failed', (response) => {
        alert(`Payment failed: ${response.error.description}`);
        setRecharging(false);
        setRechargeStatus('');
      });
      rzp.open();

    } catch (err) {
      alert(err.response?.data?.message || 'Failed to initiate payment. Please try again.');
      setRecharging(false);
      setRechargeStatus('');
    }
  };

  const formatDate = (d) => {
    const dt = new Date(d);
    const dateStr = dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    const timeStr = dt.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
    return `${dateStr}, ${timeStr}`;
  };

  if (loading) {
    return <DashboardSkeleton />;
  }

  return (
    <div className="wallet-container">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 6 }}>
        <div style={S.title}>Wallet</div>
        <span style={{ background: '#E8F5EE', color: '#1B5C3A', border: '1.5px solid #A7C9B5', padding: '6px 14px', borderRadius: 999, fontSize: 11, fontWeight: 700, letterSpacing: 0.4, display: 'flex', alignItems: 'center', gap: 6 }}>
          🔒 Razorpay Secured
        </span>
      </div>
      <div style={S.subtitle}>Manage your balance and transactions</div>

      {/* Balance Card */}
      <div className="wallet-balance-card">
        <div>
          <div style={S.balanceLabel}>Available Balance</div>
          <div style={S.balanceVal}>{'₹'}{wallet?.balance?.toLocaleString('en-IN') || 0}</div>
        </div>
        <div style={{ fontSize: 54, opacity: 0.35, userSelect: 'none' }}>💳</div>
      </div>

      {/* Sandbox Request Credit */}
      {isLocalMode && wallet?.balance < (wallet?.requestCreditThreshold || 10) && (
        <div style={{
          background: '#FFFBEB',
          border: '1.5px solid #FCD34D',
          borderRadius: 12,
          padding: '18px 24px',
          marginBottom: 20,
          animation: 'fadeIn 0.3s ease-in'
        }}>
          <h3 style={{ margin: 0, color: '#92400E', fontSize: 16, fontWeight: 700, marginBottom: 6 }}>Low Testing Balance</h3>
          <p style={{ margin: 0, color: '#B45309', fontSize: 13, lineHeight: '18px', marginBottom: 14 }}>
            Your sandbox wallet balance is low. Click the button below to request testing credits from the administrator.
          </p>
          <button
            onClick={handleRequestCredit}
            disabled={requesting || wallet?.hasPendingRequest}
            style={{
              background: wallet?.hasPendingRequest ? '#F59E0B' : '#D97706',
              color: '#fff',
              border: 'none',
              padding: '10px 20px',
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 700,
              cursor: wallet?.hasPendingRequest ? 'not-allowed' : 'pointer',
              transition: 'background 0.2s',
            }}
          >
            {requesting ? 'Requesting...' : wallet?.hasPendingRequest ? 'Request Pending' : 'Request Credit'}
          </button>
        </div>
      )}

      {/* KYC gate */}
      {!kycVerified && (
        <div style={{ background: '#FEF2F2', border: '1px solid #FCA5A5', color: '#B91C1C', borderRadius: 12, padding: '14px 18px', marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
          <span style={{ fontSize: 13, fontWeight: 600 }}>
            {isLocalMode
              ? `⚠️ KYC verification is required to receive leads. Status: ${kycStatus === 'submitted' ? 'Under Review' : kycStatus === 'rejected' ? 'Rejected' : 'Not Submitted'}`
              : `🛡️ Wallet recharge is disabled until your KYC is verified.${kycStatus === 'submitted' ? ' Your submission is under review.' : ''}${kycStatus === 'rejected' && vendorProfile?.kyc?.rejectionReason ? ` Rejected: ${vendorProfile.kyc.rejectionReason}` : ''}`
            }
          </span>
          <Link to="/vendor/kyc" style={{ background: '#B91C1C', color: '#fff', textDecoration: 'none', padding: '8px 18px', borderRadius: 8, fontSize: 12, fontWeight: 700 }}>
            {kycStatus === 'draft' ? 'Complete KYC →' : 'View KYC →'}
          </Link>
        </div>
      )}

      {/* Quick Recharge */}
      {!isLocalMode && (
        <div className="wallet-recharge-card" style={{ opacity: kycVerified ? 1 : 0.55, pointerEvents: kycVerified ? 'auto' : 'none' }}>
          <div style={S.sectionTitle}>Quick Recharge</div>

          <div style={S.presetRow}>
            {PRESETS.map(amt => (
              <button
                key={amt}
                style={S.presetBtn(selectedAmount === amt)}
                disabled={!kycVerified}
                onClick={() => { setSelectedAmount(selectedAmount === amt ? null : amt); setCustomAmount(''); }}>
                {'₹'}{amt.toLocaleString('en-IN')}
              </button>
            ))}
          </div>

          <div className="wallet-custom-row">
            <span style={{ fontSize: 13, color: 'var(--muted)', fontWeight: 600 }}>or custom:</span>
            <div className="wallet-custom-input-group">
              <span style={{ fontSize: 18, color: 'var(--char)', fontWeight: 700, flexShrink: 0 }}>{'₹'}</span>
              <input
                style={{ ...S.input, flex: '1 1 auto', width: '100%' }}
                className="form-input"
                type="number"
                placeholder={`Enter amount (min ₹${wallet?.minRecharge || 500})`}
                value={customAmount}
                disabled={!kycVerified}
                onChange={e => { setCustomAmount(e.target.value); setSelectedAmount(null); }}
                onWheel={(e) => e.target.blur()}
                min={wallet?.minRecharge || 500}
              />
              <button
                onClick={handlePayNow}
                disabled={recharging || !rechargeAmount || !kycVerified}
                style={{
                  background: '#1B5C3A', color: '#fff', border: 'none', padding: '11px 24px',
                  borderRadius: 8, fontSize: 13, fontWeight: 700,
                  cursor: (!kycVerified || recharging || !rechargeAmount) ? 'not-allowed' : 'pointer',
                  opacity: (recharging || !kycVerified) ? 0.6 : 1,
                  display: 'flex', alignItems: 'center', gap: 8,
                  flexShrink: 0
                }}>
                {recharging ? (
                  <>
                    <span style={{ display: 'inline-block', width: 14, height: 14, border: '2px solid rgba(255,255,255,0.4)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                    {rechargeStatus || 'Processing...'}
                  </>
                ) : (
                  `Pay Now${rechargeAmount ? ` ₹${rechargeAmount.toLocaleString('en-IN')}` : ''}`
                )}
              </button>
            </div>
          </div>

          {/* Razorpay secure badge */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--muted)', marginBottom: 16 }}>
            🔒 <span>Secured by Razorpay · 256-bit SSL encryption · PCI-DSS compliant</span>
          </div>

          {/* GST-inclusive preview */}
          {gstRule && rechargeAmount > 0 && (() => {
            const p = gstPreview(rechargeAmount);
            if (!p) return null;
            return (
              <div style={{ background: '#FAF8F3', border: '1px solid var(--border)', borderRadius: 10, padding: '14px', fontSize: 13, color: 'var(--char)' }}>
                <div style={{ fontWeight: 700, marginBottom: 8, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: 0.5, fontSize: 10 }}>Invoice preview (GST-inclusive)</div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}><span>Taxable value</span><span>{'₹'}{p.taxable.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span></div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                  <span>{p.isIntraState ? `CGST + SGST @ ${gstRule.totalRate}%` : `IGST @ ${gstRule.totalRate}%`}</span>
                  <span>{'₹'}{p.gst.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, borderTop: '1.5px solid var(--border)', paddingTop: 8, marginTop: 8 }}>
                  <span>You pay</span><span>{'₹'}{Number(rechargeAmount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                </div>
                <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 8, lineHeight: 1.4 }}>
                  Full {'₹'}{Number(rechargeAmount).toLocaleString('en-IN')} credited to wallet. SAC {gstRule.sacCode}. Wallet is prepaid and non-refundable.
                </div>
              </div>
            );
          })()}

          {lastInvoice && (
            <div style={{ marginTop: 16, background: '#E8F5EE', border: '1.5px solid #1B5C3A', borderRadius: 10, padding: '14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
              <div style={{ fontSize: 13, color: '#0F3D26' }}>
                ✅ Tax invoice <strong>{lastInvoice.invoiceNumber}</strong> issued for this recharge.
              </div>
              <Link to={`/vendor/invoices/${lastInvoice._id}`} style={{ background: '#1B5C3A', color: '#fff', textDecoration: 'none', padding: '8px 16px', borderRadius: 8, fontSize: 12, fontWeight: 700 }}>View invoice →</Link>
            </div>
          )}
        </div>
      )}

      {/* Transaction Ledger */}
      <div style={S.sectionTitle}>Transaction Ledger</div>
      {transactions.length === 0 ? (
        <div style={S.emptyBox}>
          <div style={{ fontSize: 40, marginBottom: 10 }}>📒</div>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--char)', marginBottom: 4 }}>No transactions yet</div>
          <div style={{ fontSize: 12, color: 'var(--muted)' }}>Recharge your wallet to get started</div>
        </div>
      ) : (
        <div style={{ background: '#fff', borderRadius: 14, border: '1.5px solid var(--border)', overflowX: 'auto', WebkitOverflowScrolling: 'touch', boxShadow: 'var(--shadow-sm)' }}>
          <table style={S.table}>
            <thead>
              <tr>
                <th style={S.th}>Date</th>
                <th style={S.th}>Description</th>
                <th style={{ ...S.th, textAlign: 'right' }}>Debit</th>
                <th style={{ ...S.th, textAlign: 'right' }}>Credit</th>
                <th style={{ ...S.th, textAlign: 'right' }}>Balance</th>
              </tr>
            </thead>
            <tbody>
              {transactions.map((t, i) => (
                <tr key={t._id || i}
                  onMouseEnter={(e) => {
                    const children = e.currentTarget.children;
                    for (let child of children) child.style.backgroundColor = '#f9fafb';
                  }}
                  onMouseLeave={(e) => {
                    const children = e.currentTarget.children;
                    for (let child of children) child.style.backgroundColor = 'transparent';
                  }}
                >
                  <td style={S.td}>{formatDate(t.createdAt || t.date)}</td>
                  <td style={{ ...S.td, fontWeight: 500 }}>{t.description || t.type || '-'}</td>
                  <td style={{ ...S.td, textAlign: 'right', color: '#DC2626', fontWeight: 700 }}>
                    {t.type === 'debit' || t.type === 'adjustment' ? `₹${Math.abs(t.amount)?.toLocaleString('en-IN')}` : '-'}
                  </td>
                  <td style={{ ...S.td, textAlign: 'right', color: 'var(--green)', fontWeight: 700 }}>
                    {t.type === 'credit' || t.type === 'refund' ? `₹${Math.abs(t.amount)?.toLocaleString('en-IN')}` : '-'}
                  </td>
                  <td style={{ ...S.td, textAlign: 'right', fontFamily: 'var(--font-serif)', fontWeight: 700 }}>
                    {'₹'}{t.runningBalance?.toLocaleString('en-IN') || '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
